import { useId, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface RadioOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface RadioGroupProps<T extends string> {
  /** Rótulo do grupo (Figma: Title — SemiBold 14). */
  label: string;
  name: string;
  value: T;
  options: readonly RadioOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Grupo de radio buttons (Figma FGP: "Radio button" — círculo 16px roxo, gap 8, itens a 20px).
 * Usa `<input type="radio">` nativo (oculto) para teclado e leitores de tela.
 */
export function RadioGroup<T extends string>({
  label,
  name,
  value,
  options,
  onChange,
  disabled,
  className,
}: RadioGroupProps<T>) {
  const labelId = useId();

  return (
    <div role="radiogroup" aria-labelledby={labelId} className={cn('flex flex-col gap-2', className)}>
      <span id={labelId} className="text-trim text-sm font-semibold leading-none text-ink">
        {label}
      </span>
      <div className="flex items-start gap-5">
        {options.map((option) => {
          const checked = option.value === value;
          return (
            <label
              key={option.value}
              className={cn(
                'group relative flex items-center gap-2',
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(option.value)}
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className="flex size-4 shrink-0 items-center justify-center rounded-full border border-brand-outline bg-surface peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-outline"
              >
                {checked && <span className="size-2 rounded-full bg-brand-solid" />}
              </span>
              <span className="text-trim text-sm leading-none text-ink">{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
